// Admin authoring view: chapter manager + Tiptap WYSIWYG with resizable images,
// manual save (⌘/Ctrl+S → DB + git), publish/unpublish, and git history.

import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { ResizableImage } from "./resizable-image";
import { api, Me } from "./api";
import { icons } from "./icons";
import { currentTheme, toggleTheme } from "./theme";

interface AdminChapter {
  id: string;
  index: number;
  title: string;
  slug: string;
  status: string;
  word_count: number;
}

export async function mountWriter(root: HTMLElement, me: Me) {
  if (!me.is_admin) {
    root.innerHTML = `<div class="center-msg">You need author access to write.<br/><a href="#/read">Back to reading</a></div>`;
    return;
  }

  // Determine the working book (single-book workflow: first book, else create).
  let books = await api.getBookList();
  if (books.length === 0) {
    root.innerHTML = createBookForm();
    document.getElementById("cbForm")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      const f = e.target as HTMLFormElement;
      const title = (f.elements.namedItem("title") as HTMLInputElement).value.trim();
      const subtitle = (f.elements.namedItem("subtitle") as HTMLInputElement).value.trim();
      const author = (f.elements.namedItem("author") as HTMLInputElement).value.trim();
      const planned = Number((f.elements.namedItem("planned") as HTMLInputElement).value || 12);
      const slug = slugify(title);
      await api.createBook({ slug, title, subtitle, author_name: author, chapters_planned: planned });
      location.reload();
    });
    return;
  }
  const book = books[0];

  root.innerHTML = `
    <div class="shell">
      <div class="topbar">
        <div class="brand"><span class="emoji">✍️</span><span class="full">${escapeHtml(book.title)}</span></div>
        <span class="save-state" id="saveState">All changes saved</span>
        <div class="spacer"></div>
        <a class="btn" href="#/read">Preview ${icons.book}</a>
        <button class="iconbtn" id="btnTheme">${currentTheme() === "dark" ? icons.sun : icons.moon}</button>
      </div>
      <div class="studio">
        <aside class="ch-list">
          <div class="ch-list-head">
            <span>Chapters</span>
            <button class="iconbtn" id="btnNewCh" title="New chapter">${icons.plus}</button>
          </div>
          <div id="chList"></div>
        </aside>
        <main class="editor-pane" id="editorPane">
          <div class="center-msg">Select or create a chapter to begin.</div>
        </main>
      </div>
    </div>`;

  document.getElementById("btnTheme")!.onclick = (e) => {
    toggleTheme();
    (e.currentTarget as HTMLElement).innerHTML = currentTheme() === "dark" ? icons.sun : icons.moon;
  };

  let chapters: AdminChapter[] = [];
  let activeId: string | null = null;
  let editor: Editor | null = null;
  let dirty = false;

  let dragId: string | null = null;

  async function refreshList() {
    chapters = (await api.listChapters(book.slug)) as AdminChapter[];
    const list = document.getElementById("chList")!;
    list.innerHTML = "";
    chapters.forEach((c) => {
      const row = document.createElement("button");
      row.className = "ch-row" + (c.id === activeId ? " active" : "");
      row.draggable = true;
      row.dataset.id = c.id;
      row.innerHTML = `
        <span class="drag-grip" title="Drag to reorder">${icons.grip}</span>
        <span class="ch-idx">${c.index + 1}</span>
        <span class="ch-title">${escapeHtml(c.title)}</span>
        <span class="ch-badge ${c.status}">${c.status === "published" ? "live" : "draft"}</span>`;
      row.onclick = () => openChapter(c.id);

      // Drag-and-drop reordering. Backend already exposes PUT …/reorder; this
      // wires the chapter sidebar to it so authors can resequence chapters.
      row.addEventListener("dragstart", (e) => {
        dragId = c.id;
        row.classList.add("dragging");
        e.dataTransfer!.effectAllowed = "move";
        e.dataTransfer!.setData("text/plain", c.id); // Firefox needs a payload
      });
      row.addEventListener("dragend", () => {
        dragId = null;
        list.querySelectorAll(".ch-row").forEach((el) =>
          el.classList.remove("dragging", "drop-before", "drop-after"));
      });
      row.addEventListener("dragover", (e) => {
        e.preventDefault();
        if (!dragId || dragId === c.id) return;
        e.dataTransfer!.dropEffect = "move";
        const r = row.getBoundingClientRect();
        const after = e.clientY > r.top + r.height / 2;
        row.classList.toggle("drop-after", after);
        row.classList.toggle("drop-before", !after);
      });
      row.addEventListener("dragleave", () =>
        row.classList.remove("drop-before", "drop-after"));
      row.addEventListener("drop", (e) => {
        e.preventDefault();
        const r = row.getBoundingClientRect();
        const after = e.clientY > r.top + r.height / 2;
        if (dragId) applyReorder(dragId, c.id, after);
      });

      list.appendChild(row);
    });
  }

  // Move `fromId` to just before/after `toId`, persist, then resync from server.
  async function applyReorder(fromId: string, toId: string, after: boolean) {
    if (fromId === toId) return;
    const ids = chapters.map((c) => c.id);
    const from = ids.indexOf(fromId);
    if (from < 0) return;
    ids.splice(from, 1);
    let to = ids.indexOf(toId);
    if (to < 0) return;
    if (after) to += 1;
    ids.splice(to, 0, fromId);

    setSaveState("Saving order…");
    try {
      await api.reorder(book.slug, ids);
      await refreshList();
      setSaveState("Order saved");
    } catch {
      setSaveState("Reorder failed");
      await refreshList();
    }
  }

  document.getElementById("btnNewCh")!.onclick = async () => {
    const title = prompt("Chapter title?", `Chapter ${chapters.length + 1}`);
    if (!title) return;
    const created = await api.createChapter(book.slug, title);
    await refreshList();
    openChapter(created.id);
  };

  async function openChapter(id: string) {
    if (dirty) await flushSave(); // don't drop unsaved edits when switching chapters
    const full = await api.getAdminChapter(id);
    activeId = id;
    await refreshList();
    renderEditor(full);
  }

  function renderEditor(ch: any) {
    const pane = document.getElementById("editorPane")!;
    pane.innerHTML = `
      <div class="editor-head">
        <input class="ch-title-input" id="chTitle" value="${escapeAttr(ch.title)}" placeholder="Chapter title" />
        <div class="editor-actions">
          <span class="word-count" id="wordCount">${ch.word_count ?? 0} words</span>
          <button class="btn" id="btnHistory">${icons.history} History</button>
          <button class="btn ${ch.status === "published" ? "" : "primary"}" id="btnPublish">
            ${ch.status === "published" ? "Unpublish" : "Publish"}
          </button>
          <button class="iconbtn danger" id="btnDelete" title="Delete chapter">${icons.trash}</button>
        </div>
      </div>
      ${toolbar()}
      <div class="editor-scroll"><div id="editor" class="prose"></div></div>
      <div class="history-panel" id="historyPanel"></div>`;

    if (editor) editor.destroy();
    editor = new Editor({
      element: document.getElementById("editor")!,
      extensions: [
        StarterKit,
        ResizableImage,
        Placeholder.configure({ placeholder: "Begin your chapter…" }),
      ],
      content: { type: "doc", content: ch.blocks?.length ? ch.blocks : [{ type: "paragraph" }] },
      onUpdate: () => markDirty(),
    });

    dirty = false;
    setSaveState("All changes saved");
    bindToolbar(editor);

    const titleInput = document.getElementById("chTitle") as HTMLInputElement;
    titleInput.oninput = () => markDirty();

    document.getElementById("btnPublish")!.onclick = async () => {
      const c = chapters.find((x) => x.id === activeId)!;
      if (c.status === "published") await api.unpublishChapter(activeId!);
      else { await flushSave(); await api.publishChapter(activeId!); }
      await openChapter(activeId!);
    };
    document.getElementById("btnDelete")!.onclick = async () => {
      if (!confirm("Delete this chapter? Past versions remain in git history.")) return;
      await api.deleteChapter(activeId!);
      activeId = null;
      await refreshList();
      document.getElementById("editorPane")!.innerHTML = `<div class="center-msg">Chapter deleted.</div>`;
    };
    document.getElementById("btnHistory")!.onclick = () => showHistory(activeId!);
  }

  function markDirty() {
    dirty = true;
    setSaveState("Unsaved changes · ⌘/Ctrl+S to save");
  }

  async function flushSave() {
    if (!editor || !activeId || !dirty) return;
    const blocks = (editor.getJSON().content as any[]) ?? [];
    const title = (document.getElementById("chTitle") as HTMLInputElement)?.value;
    setSaveState("Saving…");
    try {
      const res = await api.saveChapter(activeId, blocks, title);
      dirty = false;
      setSaveState(`Saved · ${res.commit.slice(0, 7)}`);
      const wc = document.getElementById("wordCount");
      if (wc) wc.textContent = `${res.word_count} words`;
      refreshList();
    } catch {
      setSaveState("Save failed — press ⌘/Ctrl+S to retry");
    }
  }

  async function showHistory(id: string) {
    const panel = document.getElementById("historyPanel")!;
    panel.classList.add("open");
    panel.innerHTML = `<div class="hist-head">Version history <button class="iconbtn" id="histClose">${icons.close}</button></div><div class="spinner"></div>`;
    const { commits } = await api.history(id);
    panel.innerHTML =
      `<div class="hist-head">Version history <button class="iconbtn" id="histClose">${icons.close}</button></div>` +
      (commits.length
        ? commits
            .map(
              (c) => `<div class="hist-row">
                <div class="hist-msg">${escapeHtml(c.message)}</div>
                <div class="hist-meta">${escapeHtml(c.author_name)} · ${new Date(c.when).toLocaleString()} · <code>${c.id.slice(0, 7)}</code></div>
              </div>`,
            )
            .join("")
        : `<div class="hist-row">No commits yet — save the chapter to create the first version.</div>`);
    document.getElementById("histClose")!.onclick = () => panel.classList.remove("open");
  }

  function setSaveState(s: string) {
    const el = document.getElementById("saveState");
    if (el) el.textContent = s;
  }

  // Save only on ⌘/Ctrl+S, overriding the browser's "save page" shortcut.
  // Re-mounting the writer re-binds, so drop any handler from a prior mount.
  const w = window as any;
  if (w.__writerSaveKey) document.removeEventListener("keydown", w.__writerSaveKey);
  w.__writerSaveKey = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      flushSave();
    }
  };
  document.addEventListener("keydown", w.__writerSaveKey);

  // Warn before leaving with unsaved edits (manual-save safety net).
  if (w.__writerBeforeUnload) window.removeEventListener("beforeunload", w.__writerBeforeUnload);
  w.__writerBeforeUnload = (e: BeforeUnloadEvent) => {
    if (dirty) e.preventDefault();
  };
  window.addEventListener("beforeunload", w.__writerBeforeUnload);

  await refreshList();
  if (chapters.length) openChapter(chapters[0].id);
}

// ---------------------------------------------------------------- toolbar
function toolbar(): string {
  const b = (id: string, icon: string, title: string) =>
    `<button class="tb-btn" data-cmd="${id}" title="${title}">${icon}</button>`;
  return `
    <div class="toolbar">
      ${b("h1", icons.h1, "Heading 1")}
      ${b("h2", icons.h2, "Heading 2")}
      <span class="tb-sep"></span>
      ${b("bold", icons.bold, "Bold")}
      ${b("italic", icons.italic, "Italic")}
      ${b("quote", icons.quote, "Quote")}
      ${b("bullet", icons.list, "Bullet list")}
      ${b("hr", icons.hr, "Divider")}
      <span class="tb-sep"></span>
      ${b("image", icons.image, "Insert image")}
    </div>`;
}

function bindToolbar(editor: Editor) {
  const run: Record<string, () => void> = {
    h1: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    h2: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    bold: () => editor.chain().focus().toggleBold().run(),
    italic: () => editor.chain().focus().toggleItalic().run(),
    quote: () => editor.chain().focus().toggleBlockquote().run(),
    bullet: () => editor.chain().focus().toggleBulletList().run(),
    hr: () => editor.chain().focus().setHorizontalRule().run(),
    image: () => pickImage(editor),
  };
  document.querySelectorAll<HTMLElement>(".tb-btn").forEach((btn) => {
    btn.onmousedown = (e) => {
      e.preventDefault();
      run[btn.dataset.cmd!]?.();
      syncActive(editor);
    };
  });
  editor.on("selectionUpdate", () => syncActive(editor));
  editor.on("transaction", () => syncActive(editor));
}

function syncActive(editor: Editor) {
  const map: Record<string, boolean> = {
    h1: editor.isActive("heading", { level: 1 }),
    h2: editor.isActive("heading", { level: 2 }),
    bold: editor.isActive("bold"),
    italic: editor.isActive("italic"),
    quote: editor.isActive("blockquote"),
    bullet: editor.isActive("bulletList"),
  };
  document.querySelectorAll<HTMLElement>(".tb-btn").forEach((btn) => {
    btn.classList.toggle("active", !!map[btn.dataset.cmd!]);
  });
}

function pickImage(editor: Editor) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    try {
      const { src } = await api.uploadImage(file);
      editor.chain().focus().insertContent({ type: "image", attrs: { src, width: "70%", align: "center", alt: file.name } }).run();
    } catch {
      alert("Image upload failed.");
    }
  };
  input.click();
}

// ---------------------------------------------------------------- misc
function createBookForm(): string {
  return `
    <div class="center-msg">
      <form id="cbForm" class="book-form">
        <h2>Start your book</h2>
        <input name="title" placeholder="Book title" required />
        <input name="subtitle" placeholder="Subtitle (optional)" />
        <input name="author" placeholder="Author name" />
        <input name="planned" type="number" min="1" value="12" placeholder="Chapters planned" />
        <button class="btn primary" type="submit">Create book</button>
      </form>
    </div>`;
}

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "book";
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
}
function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, "&quot;");
}
